import { useEffect, useState, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import { Search, RefreshCw, ClipboardList } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { Loader, EmptyState, PageHeader } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { OrderDetail, ORDER_STATUSES } from "./OrderDetail";

export function OrdersBoard() {
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/orders", { params: { status, search } });
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e));
      setOrders([]);
    }
  }, [status, search]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const onUpdated = (o) => {
    setOpen(o);
    setOrders((prev) => prev.map((x) => (x.id === o.id ? o : x)));
  };

  return (
    <div>
      <PageHeader title="Orders" subtitle="Live orders refresh automatically every 15 seconds.">
        <Button variant="outline" onClick={load} data-testid="orders-refresh-btn"><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
      </PageHeader>
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search order no." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="orders-search" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {["", ...ORDER_STATUSES].map((s) => (
            <button
              key={s || "all"} onClick={() => setStatus(s)} data-testid={`orders-filter-${s || "all"}`}
              className={`rounded-full px-3 py-1.5 text-xs font-medium capitalize transition-colors ${status === s ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}
            >
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {orders === null ? <Loader /> : orders.length === 0 ? (
        <EmptyState icon={ClipboardList} title="No orders yet" description="Orders placed from your QR menu will appear here." testId="orders-empty" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="orders-list">
          {orders.map((o) => (
            <button
              key={o.id} onClick={() => setOpen(o)} data-testid={`order-card-${o.order_number}`}
              className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <span className="font-heading font-semibold text-slate-900">{o.order_number}</span>
                <StatusBadge status={o.status} />
              </div>
              <p className="mt-1 text-xs text-slate-400">
                {o.table_name || "Takeaway"} · {formatDistanceToNow(new Date(o.created_at), { addSuffix: true })}
              </p>
              <p className="mt-3 line-clamp-2 text-sm text-slate-600">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
              <div className="mt-3 flex items-center justify-between">
                <span className="font-heading text-lg font-bold text-slate-900">{money(o.grand_total)}</span>
                <StatusBadge status={o.payment_status} />
              </div>
            </button>
          ))}
        </div>
      )}
      <OrderDetail order={open} onClose={() => setOpen(null)} onUpdated={onUpdated} />
    </div>
  );
}
